import {
  IWidgetConfigurationProps,
  withWidgetConfiguration,
} from "@sgwt-widget/core";
import { ColDef, IHeader } from "ag-grid-community";
import { isEqual, isArray } from "lodash";
import { h } from "preact";
import { ApiBackedComponent } from "../../common/components/ApiBackedComponent";
import { MakeGenericApiCall } from "../../common/generic/generic-api";
import {
  CheckReponseDataForError,
  GenericFilterBuilder,
} from "../../common/generic/generic-helper";
import { IGenericRequest } from "../../common/generic/generic-types";
import { ConvertFilter } from "../../common/salescredit/salescredit-filter-help";
import {
  BuildSalesCreditFilters,
  CreateDateFromFilter,
} from "../../common/salescredit/salescredit-search-criteria";
import { IHeaders } from "../../common/utils/types";
import { Grid } from "../grid/cpr-ag-grid";
import { getMapping, getParams, getSortByKpi } from "./utils/helper";
import { AgGridUserPreference } from "../../common/sg-dashboard/userPreference/userPreference.typings";
import { ObsoleteWidgetMessage } from "../../common/utils/ObsoleteWidgetMessage";

export interface IProps {
  attribute?: string;
  filters?: { columns: string[]; [attribute: string]: string[] | string } | any;
  headers: IHeaders;
  userSelection?: AgGridUserPreference;
  onUserConfigChanged?: (config: AgGridUserPreference) => void;
}

interface IState {
  data: any[];
  colmunsDef: IHeader[];
}

export class SalesCreditProfitabilityClass extends ApiBackedComponent<
  IProps & IWidgetConfigurationProps,
  IState
> {
  public constructor() {
    super();
    this.setState({
      data: [],
    });
  }

  protected shouldFetchApiData(nextProps: IProps): boolean {
    if (isEqual(this.props.filters, nextProps.filters)) {
      return false;
    }
    return true;
  }

  protected renderWithDataReady(): JSX.Element {
    const { colmunsDef, data } = this.state;
    return (
      <div>
        <ObsoleteWidgetMessage newWidgetId={2440} />
        <div style="height: 98%;">
          <Grid
            columnDefs={colmunsDef}
            rowData={data}
            onColumnConfigChanged={this.handleUserConfigChanged.bind(this)}
          />
        </div>
      </div>
    );
  }

  protected async fetchApiData(props: IProps & IWidgetConfigurationProps) {
    this.initCorrelationId();
    const { filters, headers, widgetConfiguration, userSelection } = props;

    this.setDataStatus("loading");

    const filtersConverted = ConvertFilter(filters);
    const scFilters = BuildSalesCreditFilters(filtersConverted);

    if (!filters.columns || filters.columns.length < 1) {
      return;
    }

    const [columnsHeader, attributesSelected, kpisSelected] = getParams(
      filters.columns,
      userSelection
    );

    if (!isArray(kpisSelected) || kpisSelected.length === 0) {
      this.setDataStatus("no-kpi-selected");
      return;
    }

    const genericRequest: IGenericRequest = {
      aggregationAttributes: attributesSelected.join(","),
      date: CreateDateFromFilter(filtersConverted),
      filter: GenericFilterBuilder(scFilters),
      kpis: kpisSelected.join(","),
      sort: "-" + getSortByKpi(kpisSelected),
    };

    const responseData = await MakeGenericApiCall(
      headers,
      "sc",
      genericRequest,
      widgetConfiguration,
      this.state.correlationId
    );

    const errorStatus = CheckReponseDataForError(responseData);
    if (errorStatus) {
      this.setDataStatus(errorStatus);
      this.setStatusText(responseData.statusText);
      this.setState({
        data: [],
      });
      return;
    }

    const rowData = getMapping(responseData, attributesSelected, kpisSelected);
    this.setState({ data: rowData, colmunsDef: columnsHeader });
    this.setDataStatus("ready");
  }

  private handleUserConfigChanged(columnConfig: ColDef[]) {
    const useConfig: AgGridUserPreference = { columns: columnConfig };

    if (this.props.onUserConfigChanged) {
      this.props.onUserConfigChanged(useConfig);
    }
  }
}

export const SalesCreditProfitability = withWidgetConfiguration(
  SalesCreditProfitabilityClass
);
